
import axios from "axios";
import { ExistingFile, NewFile } from "../components/shared/FileUpload/FileUpload";

export type TedContractNature = "works" | "services" | "supplies";

export type TedNoticeCategory = {
  superAdminAreaId: string;
  name: string;
}

export type TedNotice = {
  id: string;
  publicationNumber: string;
  title: string;
  description?: string | null;
  buyer: { name: string | null, city?: string | null, email?: string | null, url?: string | null };
  category: TedNoticeCategory | null;
  categories: TedNoticeCategory[];
  cpv: { code: string | null, label: string | null };
  cpvCodes?: { code: string, label: string | null }[];
  places: string[];
  placesAreApproximate: boolean;
  contractNature: TedContractNature | null;
  procedureType: string | null;
  publicationDate: string;
  deadline: string | null;
  deadlineType: "tender" | "request" | null;
  deadlineLocalDate: string | null;
  estimatedValue: number | null;
  estimatedValueCurrency: string | null;
  noticeUrl: string;
  noticePdfUrl?: string | null;
  submissionUrl: string | null;
  documentUrl: string | null;
}

export type TedNoticeQuery = {
  search?: string;
  areaIds?: string[];
  cpvCodes?: string[];
  buyers?: string[];
  places?: string[];
  contractNature?: string[];
  page: number;
  pageSize: number;
  sort?: string;
}

export type TedCpvCode = {
  code: string;
  label: string;
  division: string;
  divisionLabel: string;
}

export type TedCategory = {
  id: string;
  name: string;
  cpvCodes: string[];
}

export type TedNoticePage = {
  data: TedNotice[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type Area = {
  _id: string;
  name: string;
}

export type Contact = {
  _id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
}

export type OtherEmailAdress = {
  email: string;
  comment: string;
  redirectToId?: string;
  count: number;
  orgs: string[]
}


export type LiabilityInsuranceItem = {
  _id?: string;
  file?: {
    fileName: string;
    file: string;
    mimeType: string;
  };
  validUntilDate?: Date;
  comment: string;
  replacementFile?: File;
};

export type SupplierProfile = {
  name: string;
  orgNr?: string;
  logo?: {
    file: string;
    name: string;
  };
  newLogo?: File;
  presentation: string;
  address: string;
  website: string;
  areas: Area[];
  places: string[];
  contacts: Contact[];
  otherEmailAddresses: OtherEmailAdress[];
  liabilityInsurances: LiabilityInsuranceItem[];
  existingLiabilityInsurances?: LiabilityInsuranceItem[];
  newLiabilityInsurances?: NewFile[];
  existingFiles: ExistingFile[];
  newFiles: NewFile[];
}

export type Invoice = {
  _id: string,
  organization: string,
  product: string,
  startDate: Date,
  endDate: Date,
  invoiceNumber: string,
  cause: "ORDER" | "RENEWAL",
  amount: string,
}

export type SubscriptionEvent = {
  _id: string,
  action: "ORDER" | "RENEW" | "CANCEL" | "UNCANCEL" | "EXPIRE",
  date: Date,
  user: string,
  price: string,
  duration: string,
  durationUnit: "MONTH" | "WEEK",
}

export type Subscription = {
  active?: {
    startDate: Date, 
    endDate: Date, 
    autoRenew: boolean,
    price: string,
    duration: string,
    durationUnit: "MONTH" | "WEEK",
    adId: string,
    cancelAt: Date,
    cancellationDelay: string,
  }, 
  invoices: Invoice[],
  history: SubscriptionEvent[],
}


class ServiceSales {
  async saveSupplierProfile(supplierProfile : SupplierProfile) {
    const formData = new FormData();
    const { newLogo, newLiabilityInsurances, newFiles, existingLiabilityInsurances, ...json } = supplierProfile;

    const existingForJson = existingLiabilityInsurances?.filter(e => !e.replacementFile).map(({ replacementFile, file, ...e }) => ({
      ...e,
      fileRemoved: !file,
    })) || [];

    const newMetadata = newLiabilityInsurances?.map(({ file, ...metadata }) => metadata) || [];

    const replacementMetadata = existingLiabilityInsurances?.filter(e => e.replacementFile).map(({ replacementFile, file, ...meta }) => ({ validUntilDate: meta.validUntilDate, comment: meta.comment })) || [];
    const allNewMetadata = [...newMetadata, ...replacementMetadata];

    formData.set("json", JSON.stringify({ ...json, existingLiabilityInsurances: existingForJson, newLiabilityInsurances: allNewMetadata }));
    if (newLogo) formData.set("newLogo", newLogo);

    if (newLiabilityInsurances) {
      for (let i = 0; i < newLiabilityInsurances.length; i++) {
        if (newLiabilityInsurances[i].file) {
          formData.append("newLiabilityInsurances", newLiabilityInsurances[i].file);
        }
      }
    }
    if (existingLiabilityInsurances) {
      for (let i = 0; i < existingLiabilityInsurances.length; i++) {
        const file = existingLiabilityInsurances[i].replacementFile;
        if (file) {
          formData.append("newLiabilityInsurances", file);
        }
      }
    }
    if (newFiles && newFiles.length > 0) {
      for (let i = 0; i < newFiles.length; i++) {
        formData.append("newFiles", newFiles[i].file);
      }
    }
    const response = await axios.put("/api/sales/supplier-profile", formData);
    return response.data;
  }

  async fetchSupplierProfile() : Promise<[SupplierProfile, Area[]]> {
    const response = await axios.get("/api/sales/supplier-profile");
    const {areaOptions, ...supplierProfile } = response.data;
    return [supplierProfile, areaOptions];
  }

  async fetchSupplierProfileForOrgNumber(orgNumber : string) : Promise<SupplierProfile> {
    const response = await axios.get(`/api/sales/supplier-profile/${orgNumber}`);
    return response.data;
  }

  async fetchAllQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.get("/api/sales/all-quotations/");
    return response.data;
  }

  async orderAllQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/all-quotations/order");
    return response.data;
  }

  async cancelAllQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/all-quotations/cancel");
    return response.data;
  }

  async fetchRecommendedSubscription(): Promise<Subscription> {
    const response = await axios.get("/api/sales/recommended/");
    return response.data;
  }

  async orderRecommendedSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/recommended/order");
    return response.data;
  }

  async cancelRecommendedSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/recommended/cancel");
    return response.data;
  }

  async hardCancelRecommendedSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/recommended/hard-cancel");
    return response.data;
  }

  async fetchPublishedQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.get("/api/sales/published-quotations/");
    return response.data;
  }

  async orderPublishedQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/published-quotations/order");
    return response.data;
  }

  async cancelPublishedQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/published-quotations/cancel");
    return response.data;
  }

  async hardCancelPublishedQuotationsSubscription(): Promise<Subscription> {
    const response = await axios.post("/api/sales/published-quotations/hard-cancel");
    return response.data;
  }

  async fetchInvoices(): Promise<Invoice[]> {
    const response = await axios.get("/api/sales/invoices");
    return response.data;
  }

  async saveInvoiceNumber(_id: string, invoiceNumber: string) {
    const response = await axios.put("/api/sales/invoices/" + _id, { invoiceNumber });
    return response.data;
  }

  async hasProfileBeenUpdated() {
    const response = await axios.get("/api/sales/supplier-profile-has-been-updated");
    return response.data;
  }

  async fetchSuperAdminAreas(): Promise<{ _id: string, name: string }[]> {
    const response = await axios.get("/api/sales/super-admin-areas");
    return response.data;
  }

  async fetchTedNotices(query: TedNoticeQuery): Promise<TedNoticePage> {
    const response = await axios.get("/api/sales/ted/notices", {
      params: query,
      // Repeat the key instead of the axios default "areaIds[]=", which the backend's
      // query parsing does not recognise.
      paramsSerializer: { indexes: null },
    });
    return response.data;
  }

  async fetchTedCpvCodes(): Promise<TedCpvCode[]> {
    const response = await axios.get("/api/sales/ted/cpv-codes");
    return response.data;
  }

  async fetchTedBuyers(): Promise<string[]> {
    const response = await axios.get("/api/sales/ted/buyers");
    return response.data;
  }

  async fetchTedCategories(): Promise<TedCategory[]> {
    const response = await axios.get("/api/sales/ted/categories");
    return response.data;
  }

  async fetchTedNotice(publicationNumber: string): Promise<TedNotice> {
    const response = await axios.get(`/api/sales/ted/notices/${encodeURIComponent(publicationNumber)}`);
    return response.data;
  }
} 

export default new ServiceSales();
